import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Calendar,
  Star,
  User,
} from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AllVideoCollectionThunk, DeleteVideoCollectionThunk } from "../../redux/states/videoCollection/thunk";

const ManageVideoCollection = () => {
  const dispatch = useDispatch();
  const { videocollection, loading, error } = useSelector((state) => state.videocollection);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredvideocollection, setFilteredVideocollection] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(AllVideoCollectionThunk());
  }, [dispatch]);

useEffect(() => {
  if (videocollection?.length > 0) {
    const filtered = videocollection.filter((r) => {
      const term = searchTerm.toLowerCase();

      return (
        r.user_name?.toLowerCase().includes(term) ||
        r.title?.toLowerCase().includes(term) ||
        r.comment?.toLowerCase().includes(term) ||
        r.rating?.toString().includes(term) ||
        r.status?.toLowerCase().includes(term)
      );
    });

    setFilteredVideocollection(filtered);
  } else {
    setFilteredVideocollection([]);
  }
}, [videocollection, searchTerm]);


  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteVideoCollectionThunk(selectedId))
        .unwrap()
        .then(() => {
          toast.success("Video Collection deleted successfully!");
          dispatch(AllVideoCollectionThunk());
        })
        .catch(() => {
          toast.error("Failed to Delete Video Collection");
        });
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleView = (uuid) => {
    navigate(`/view-video-collection/${uuid}`);
  };

  const handleEdit = (uuid) => {
    navigate(`/update-new-video-collection/${uuid}`);
  };

  const handleAdd = () => {
    navigate(`/add-new-video-collection`);
  };

  const handleRefresh = () => {
    dispatch(AllVideoCollectionThunk());
    toast.success("Video Collection list refreshed!");
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-2">
        {/* Header Section */}
        <div className="mb-3 flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Star className="w-8 h-8 text-yellow-500" />
              Product Video Collection
            </h1>
            <p className="text-gray-600 mt-2">
              List and manage all Product collection
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300"
            >
              + Add Video Collection
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-300"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Card */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-2 mb-2">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Video Collection
                </p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {filteredvideocollection.length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-xl">
                <Star className="w-6 h-6 text-yellow-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="text-gray-400 w-5 h-5" />
              </div>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-300"
                placeholder="Search by name, email, or rating..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300">
              <Filter className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-scroll">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin mb-2" />
                        <p className="text-gray-600">Loading reviews...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredvideocollection.length > 0 ? (
                  filteredvideocollection?.map((review, index) => (
                    <tr
                      key={review?.uuid}
                      className="hover:bg-gray-50 transition-colors duration-200 group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap flex items-center gap-3">
                        <div className="bg-yellow-100 p-2 rounded-lg">
                          <User className="w-4 h-4 text-yellow-500" />
                        </div>
                        <div>
                          <p className="font-semibold">
                            {review?.product_id_data || "N/A"}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate">
                        <span
                          className={`font-medium px-3 py-1 rounded-full 
                                ${
                                  review.status === "active"
                                    ? "text-green-700 bg-green-100"
                                    : review.status === "deleted"
                                    ? "text-red-700 bg-red-100"
                                    : "text-gray-600 bg-gray-100"
                                }`}
                        >
                          {review.status || "No Status"}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <Calendar className="w-4 h-4 inline mr-2 text-gray-400" />
                        {review.created_at
                          ? new Date(review?.created_at).toLocaleDateString(
                              "en-IN"
                            )
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleView(review.uuid)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(review.uuid)}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(review.uuid)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <AlertCircle className="w-12 h-12 mb-3 text-gray-400" />
                        <p className="text-lg font-medium mb-2">
                          No video reviews found
                        </p>
                        <p className="text-sm">
                          No video reviews matched your search
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Modal */}
        <ConfirmDeleteModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Video Collection"
          message="Are you sure you want to delete this video Collection? This action cannot be undone."
        />
      </div>
    </NonAuthLayout>
  );
};

export default ManageVideoCollection;
